import {
  Box,
  TextInput,
  Heading,
  Text,
  RadioGroup,
  Radio,
  Divider,
  PlusIcon,
  Button,
  TrashIcon,
  Tooltip,
  TooltipInteractiveWrapper,
  InfoIcon,
} from '@razorpay/blade/components';

import React, { useState } from 'react';
import { useFormWithProvider } from 'forms';
import useFeature from 'hooks/useFeature';
import {
  BenefitAmountSettings,
  BenefitAmountType,
  BenefitAmountUnit,
  BenefitTier,
  DeclarationMethod,
  FlexibleBenefitFormData,
} from './types';
import { MultiBenefitValueRow, SelectAmountContainer } from './styles';
import { isEmployerNPSComponent } from './utils';
import { overlayLayers } from 'constants/overlayLayers.generated';

export const AmountDetails = ({ isEditFlow = false }: { isEditFlow?: boolean }) => {
  const { formData, errors, setFormData, clearAllErrors, setFieldError, clearFieldError } =
    useFormWithProvider<FlexibleBenefitFormData>();

  const [multipleOptionInputValue, setMultipleOptionInputValue] = useState('');

  const handleDeclarationSettingsChange = (value: DeclarationMethod) => {
    setFormData({
      ...formData,
      declaration_settings: {
        declaration_enabled: value,
      },
      benefit_amount_settings: {
        ...formData.benefit_amount_settings,
        ...(value === 'true'
          ? { type: 'RANGE', unit: 'ACTUAL_AMOUNT', value: 0 }
          : { type: 'FIXED', value: 0 }),
      },
    });
  };

  const formatOptions = (options: Array<number>): string => {
    if (options.length === 0) return '';
    if (options.length === 1) return options[0].toString();
    if (options.length === 2) return `${options[0]} and ${options[1]}`;
    return `${options.slice(0, -1).join(', ')} and ${options[options.length - 1]}`;
  };

  const setBenefitsAmountData = (updatedValue: BenefitAmountSettings) => {
    clearAllErrors();
    setFormData({
      ...formData,
      benefit_amount_settings: {
        ...formData.benefit_amount_settings,
        ...updatedValue,
      },
    });
  };

  const onMultipleValueInputAdd = () => {
    if (!multipleOptionInputValue) {
      setFieldError('multipleValuesList', 'Please enter a value');
      return;
    }

    if (!/^\d+$/.test(multipleOptionInputValue)) {
      setFieldError('multipleValuesList', 'Please enter a valid number without decimals');
      return;
    }

    const numericValue = Number(multipleOptionInputValue);
    if (numericValue < 0) {
      setFieldError('multipleValuesList', 'Please enter a positive number');
      return;
    }

    if (formData.benefit_amount_settings?.options?.includes(Number(multipleOptionInputValue))) {
      setFieldError('multipleValuesList', 'Value already exists');
      return;
    }

    setBenefitsAmountData({
      options: [
        ...(formData?.benefit_amount_settings?.options || []),
        Number(multipleOptionInputValue),
      ],
      default_option_index: noMultiValueOptionsAdded
        ? 0
        : formData?.benefit_amount_settings?.default_option_index,
    });
    setMultipleOptionInputValue('');
  };

  const isMultiValuesSelected =
    formData?.benefit_amount_settings?.type === 'LIST_OF_VALUES' &&
    (formData?.benefit_amount_settings?.options?.length ?? 0) > 0;

  const isPercentageOfBasicSelected =
    formData?.benefit_amount_settings?.unit === 'PERCENTAGE_OF_BASIC';

  const isFixedAmountSelected = formData?.benefit_amount_settings?.unit === 'ACTUAL_AMOUNT';

  const noMultiValueOptionsAdded = (formData?.benefit_amount_settings?.options?.length ?? 0) === 0;

  const isMaxLimitSelected = formData.benefit_amount_settings?.type === 'RANGE';

  const isTieredSelected = formData.benefit_amount_settings?.type === 'TIERED_RANGE';

  const tiers = formData.benefit_amount_settings?.tiered_range_amount?.tiers ?? [];

  const hasUnsafeTierLabels = tiers.some((tier) => /[<>]/.test(tier.label));

  const hasDuplicateTierLabels = (() => {
    const labels = tiers
      .map((tier) => tier.label.trim().toLowerCase())
      .filter((label) => label !== '');
    return new Set(labels).size !== labels.length;
  })();

  const setTiers = (nextTiers: Array<BenefitTier>, nextDefaultIndex?: number) =>
    setBenefitsAmountData({
      tiered_range_amount: {
        tiers: nextTiers,
        default_tier_index:
          nextDefaultIndex ??
          formData.benefit_amount_settings?.tiered_range_amount?.default_tier_index ??
          0,
      },
    });

  const makeEmptyTier = (index: number): BenefitTier => ({
    id: `t_${Date.now().toString(36)}_${index}`,
    label: '',
    min: 0,
    max: 0,
  });

  const addTier = () => setTiers([...tiers, makeEmptyTier(tiers.length)]);

  const updateTier = (index: number, patch: Partial<BenefitTier>) =>
    setTiers(tiers.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)));

  const removeTier = (index: number) => {
    const remaining = tiers.filter((_, i) => i !== index);
    const currentDefault =
      formData.benefit_amount_settings?.tiered_range_amount?.default_tier_index ?? 0;
    const nextDefault =
      currentDefault >= remaining.length ? Math.max(remaining.length - 1, 0) : currentDefault;
    setTiers(remaining, nextDefault);
  };

  const isAutoApplyFullAmount = formData.declaration_settings?.declaration_enabled === 'false';

  const [isHoveredOption, setIsHoveredOption] = useState<number>();

  const isNPSComponent = isEmployerNPSComponent(formData?.name || '');

  const { isFbpExcludeFromSaAdjustmentEnabled } = useFeature('fbpExcludeFromSaAdjustment');

  // Components not adjusted from Special Allowance always use employee declaration;
  // the declaration method is forced to "Enable Declaration" and hidden.
  const isAdjustToSaDisabled =
    isFbpExcludeFromSaAdjustmentEnabled &&
    formData?.sa_adjustment_settings?.adjust_to_sa === false;

  const handleSaAdjustmentChange = (adjustToSa: boolean) => {
    setFormData({
      ...formData,
      sa_adjustment_settings: {
        ...formData.sa_adjustment_settings,
        adjust_to_sa: adjustToSa,
      },
      // The exemption for non-SA-adjusted components is always reported under
      // section 10(14), and the declaration method is forced to employee
      // declaration (its selection is hidden below).
      ...(!adjustToSa
        ? {
            tax_behaviour: {
              ...formData.tax_behaviour,
              tax_exemption_section: 'section_10_14',
            },
            declarationMethod: 'true' as const,
            declaration_settings: {
              ...formData.declaration_settings,
              declaration_enabled: 'true' as const,
            },
          }
        : {}),
    });
  };

  return (
    <Box padding="spacing.6" display="flex" flexDirection="column" gap="spacing.6">
      <Box>
        <Heading size="large" weight="semibold" color="surface.text.gray.normal">
          Amount Details
        </Heading>
      </Box>

      <Box display="flex" flexDirection="column" gap="spacing.6" flex={1}>
        {isFbpExcludeFromSaAdjustmentEnabled && formData.showInGrossSalary && (
          <>
            <Box display="flex" flexDirection="row">
              <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
                <Text size="large" weight="semibold" color="surface.text.gray.normal">
                  Special Allowance Adjustment
                </Text>
              </Box>
              <Box display="flex" flexDirection="column" gap="spacing.6" flex={1}>
                <RadioGroup
                  name="sa-adjustment"
                  size="medium"
                  isDisabled={isEditFlow}
                  value={formData.sa_adjustment_settings?.adjust_to_sa === false ? 'false' : 'true'}
                  onChange={({ value }) => handleSaAdjustmentChange(value === 'true')}>
                  <Radio
                    value="true"
                    helpText="The benefit amount is deducted from Special Allowance and appears as its own line item in the payslip's earnings section.">
                    Show as a separate earning
                  </Radio>
                  <Radio
                    value="false"
                    helpText="The benefit amount stays part of Special Allowance and won't appear as a separate earning. Its tax exemption appears under Tax Deductions (Section 10(14)).">
                    Keep within Special Allowance
                  </Radio>
                </RadioGroup>
              </Box>
            </Box>

            <Divider />
          </>
        )}

        {!isAdjustToSaDisabled && (
          <>
            <Box display="flex" flexDirection="row">
              <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
                <Text size="large" weight="semibold" color="surface.text.gray.normal">
                  Declaration Method
                </Text>
              </Box>
              <Box display="flex" flexDirection="column" gap="spacing.6" flex={1}>
                <RadioGroup
                  name="declaration-method"
                  size="medium"
                  isDisabled={isEditFlow && isNPSComponent}
                  value={`${formData?.declaration_settings?.declaration_enabled}`}
                  onChange={({ value }) =>
                    handleDeclarationSettingsChange(value as DeclarationMethod)
                  }>
                  <Radio value="true" helpText="Employees can declare an amount of their choice.">
                    Enable Declaration
                  </Radio>
                  <Radio
                    value="false"
                    helpText="The maximum eligible amount will be applied automatically">
                    Auto-Apply Full Amount
                  </Radio>
                </RadioGroup>
              </Box>
            </Box>

            <Divider />
          </>
        )}

        {isAutoApplyFullAmount ? (
          <Box display="flex" flexDirection="row">
            <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
              <Text size="large" weight="semibold" color="surface.text.gray.normal">
                Define Benefit Amount
              </Text>
            </Box>
            <Box display="flex" flexDirection="column" gap="spacing.7" flex={1}>
              <RadioGroup
                name="benefit-unit"
                size="medium"
                value={formData?.benefit_amount_settings?.unit || 'ACTUAL_AMOUNT'}
                onChange={({ value }) =>
                  setBenefitsAmountData({
                    unit: value as BenefitAmountUnit,
                    value: 0,
                  })
                }>
                <Box display="flex" flexDirection="row" gap="spacing.5">
                  <SelectAmountContainer isSelected={isFixedAmountSelected}>
                    <Radio value="ACTUAL_AMOUNT">Fixed Amount</Radio>
                  </SelectAmountContainer>
                  <SelectAmountContainer isSelected={isPercentageOfBasicSelected}>
                    <Radio value="PERCENTAGE_OF_BASIC">% of Basic</Radio>
                  </SelectAmountContainer>
                </Box>
              </RadioGroup>
              <Box>
                <TextInput
                  accessibilityLabel={isPercentageOfBasicSelected ? 'Percentage' : 'Amount'}
                  placeholder={isPercentageOfBasicSelected ? '10' : '2400'}
                  prefix={isPercentageOfBasicSelected ? undefined : '₹'}
                  suffix={isPercentageOfBasicSelected ? '% of Basic' : undefined}
                  value={formData?.benefit_amount_settings?.value?.toString() || ''}
                  onChange={({ value }) =>
                    setBenefitsAmountData({
                      value,
                    })
                  }
                  name="benefit-amount"
                  size="medium"
                  validationState={
                    errors.maxLimitAmount || errors.maxLimitPercentage ? 'error' : 'none'
                  }
                  errorText={errors.maxLimitAmount || errors.maxLimitPercentage}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <Box display="flex" flexDirection="row">
              <Box display="flex" flexDirection="column" gap="spacing.2" flex={1}>
                <Text size="large" weight="semibold" color="surface.text.gray.normal">
                  Set Benefit Amount
                </Text>
              </Box>
              <Box display="flex" flexDirection="column" gap="spacing.6" flex={1}>
                <RadioGroup
                  name="type"
                  size="medium"
                  value={formData?.benefit_amount_settings?.type || 'RANGE'}
                  isDisabled={isEditFlow && isNPSComponent}
                  onChange={({ value }) =>
                    setBenefitsAmountData({
                      type: value as BenefitAmountType,
                      ...(value === 'RANGE' ? { options: undefined } : {}),
                      ...(value === 'LIST_OF_VALUES' ? { value: 0, unit: 'ACTUAL_AMOUNT' } : {}),
                      ...(value === 'TIERED_RANGE'
                        ? {
                            unit: 'ACTUAL_AMOUNT' as BenefitAmountUnit,
                            tiered_range_amount: {
                              tiers:
                                tiers.length > 0
                                  ? tiers
                                  : [makeEmptyTier(0), makeEmptyTier(1)],
                              default_tier_index: 0,
                            },
                          }
                        : {}),
                    })
                  }>
                  <Radio
                    value={
                      formData?.declaration_settings?.declaration_enabled === 'true'
                        ? 'RANGE'
                        : 'FIXED'
                    }
                    helpText="Define a maximum limit, employee can declare any value between 0 to maximum limit">
                    Set Maximum Limit
                  </Radio>
                  <Radio
                    value="LIST_OF_VALUES"
                    helpText="Define multiple values for employees to choose a single option from the dropdown.">
                    Multiple Values List
                  </Radio>
                  {isFbpExcludeFromSaAdjustmentEnabled && (
                    <Radio
                      value="TIERED_RANGE"
                      helpText="Define a value per tier, Employee's tier decides the limit">
                      Tier based value
                    </Radio>
                  )}
                </RadioGroup>
              </Box>
            </Box>

            <Divider />

            {isTieredSelected ? (
              <Box display="flex" flexDirection="row">
                <Box flex={1}>
                  <Text color="surface.text.gray.subtle" weight="semibold">
                    Set Limits
                  </Text>
                </Box>
                <Box display="flex" flexDirection="column" gap="spacing.3" flex={1}>
                  {/* Column headers, per design — the fields themselves are unlabelled */}
                  <Box display="flex" flexDirection="row" gap="spacing.3">
                    <Box flex={1}>
                      <Text size="small" color="surface.text.gray.muted">
                        Label Value
                      </Text>
                    </Box>
                    <Box flex={1}>
                      <Text size="small" color="surface.text.gray.muted">
                        Max Value
                      </Text>
                    </Box>
                    {/* spacer aligning headers with the per-row delete control */}
                    <Box width="36px" />
                  </Box>

                  {tiers.map((tier, index) => (
                    <Box
                      key={tier.id}
                      display="flex"
                      flexDirection="row"
                      gap="spacing.3"
                      alignItems="center">
                      <Box flex={1}>
                        <TextInput
                          accessibilityLabel={`Tier ${index + 1} label`}
                          name={`tier-label-${index}`}
                          placeholder="Below 1600 CC"
                          value={tier.label}
                          isDisabled={isEditFlow && isNPSComponent}
                          onChange={({ value }) => updateTier(index, { label: value ?? '' })}
                        />
                      </Box>
                      <Box flex={1}>
                        <TextInput
                          accessibilityLabel={`Tier ${index + 1} max value`}
                          name={`tier-max-${index}`}
                          type="number"
                          placeholder="1600"
                          value={tier.max ? String(tier.max) : ''}
                          isDisabled={isEditFlow && isNPSComponent}
                          onChange={({ value }) => updateTier(index, { max: Number(value ?? 0) })}
                        />
                      </Box>
                      <Button
                        variant="tertiary"
                        size="medium"
                        icon={TrashIcon}
                        accessibilityLabel={`Remove tier ${index + 1}`}
                        isDisabled={isEditFlow && isNPSComponent}
                        onClick={() => removeTier(index)}
                      />
                    </Box>
                  ))}

                  <Box display="flex" flexDirection="row">
                    <Button
                      variant="tertiary"
                      size="medium"
                      icon={PlusIcon}
                      iconPosition="left"
                      isDisabled={isEditFlow && isNPSComponent}
                      onClick={addTier}>
                      Add Tier
                    </Button>
                  </Box>

                  {tiers.length < 2 && (
                    <Text size="small" color="feedback.text.negative.intense">
                      Add at least 2 tiers. A single tier is just a maximum limit.
                    </Text>
                  )}
                  {hasDuplicateTierLabels && (
                    <Text size="small" color="feedback.text.negative.intense">
                      Tier names must be unique — bulk uploads resolve a tier by its name.
                    </Text>
                  )}
                  {hasUnsafeTierLabels && (
                    <Text size="small" color="feedback.text.negative.intense">
                      Tier names cannot contain &lt; or &gt;. Use wording like &quot;Below 1600
                      CC&quot; instead.
                    </Text>
                  )}
                </Box>
              </Box>
            ) : (
            <Box display="flex" flexDirection="row">
              <Box flex={1}>
                <Text color="surface.text.gray.subtle" weight="semibold">
                  {isMaxLimitSelected ? 'Set a Max limit' : 'Add multiple of value '}
                </Text>
              </Box>
              {isMaxLimitSelected ? (
                <Box display="flex" flexDirection="column" gap="spacing.5" flex={1}>
                  <RadioGroup
                    name="unit"
                    size="medium"
                    value={formData?.benefit_amount_settings?.unit || 'ACTUAL_AMOUNT'}
                    isDisabled={isEditFlow && isNPSComponent}
                    onChange={({ value }) =>
                      setBenefitsAmountData({
                        unit: value as BenefitAmountUnit,
                        value: 0,
                      })
                    }>
                    <Box display="flex" flexDirection="row" gap="spacing.5">
                      <SelectAmountContainer isSelected={isFixedAmountSelected}>
                        <Radio value="ACTUAL_AMOUNT">Fixed Amount</Radio>
                      </SelectAmountContainer>
                      <SelectAmountContainer isSelected={isPercentageOfBasicSelected}>
                        <Radio value="PERCENTAGE_OF_BASIC">% of Basic</Radio>
                      </SelectAmountContainer>
                    </Box>
                  </RadioGroup>
                  <Box>
                    <TextInput
                      accessibilityLabel={isPercentageOfBasicSelected ? 'Percentage' : 'Amount'}
                      placeholder={isPercentageOfBasicSelected ? '10' : '2000'}
                      prefix={isPercentageOfBasicSelected ? undefined : '₹'}
                      suffix={isPercentageOfBasicSelected ? '% of Basic' : undefined}
                      value={formData?.benefit_amount_settings?.value?.toString() || ''}
                      onChange={({ value }) =>
                        setBenefitsAmountData({
                          value,
                          options: undefined,
                        })
                      }
                      name="max-limit-amount"
                      size="medium"
                      validationState={
                        errors.maxLimitAmount || errors.maxLimitPercentage ? 'error' : 'none'
                      }
                      isDisabled={isEditFlow && isNPSComponent}
                      errorText={errors.maxLimitAmount || errors.maxLimitPercentage}
                    />
                  </Box>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" width={'50%'}>
                  <Box flex={1} flexDirection={'row'} gap="spacing.3" display={'flex'}>
                    <Box flex={1}>
                      <TextInput
                        isDisabled={isEditFlow && isNPSComponent}
                        value={multipleOptionInputValue || ''}
                        prefix="₹"
                        type="number"
                        label=""
                        accessibilityLabel="Benefit value"
                        onChange={({ value }) => {
                          setMultipleOptionInputValue(value || '');
                          if (errors.multipleValuesList) {
                            clearFieldError('multipleValuesList');
                          }
                        }}
                        validationState={errors.multipleValuesList ? 'error' : 'none'}
                        errorText={errors.multipleValuesList}
                      />
                    </Box>
                    <Button
                      variant="secondary"
                      icon={PlusIcon}
                      color="white"
                      size="medium"
                      accessibilityLabel="Add benefit value"
                      onClick={onMultipleValueInputAdd}
                    />
                  </Box>
                  {isMultiValuesSelected ? (
                    <Box
                      borderRadius={'medium'}
                      borderColor={'surface.border.gray.muted'}
                      borderWidth={'thin'}
                      marginTop={'spacing.6'}>
                      <Box
                        paddingY={'spacing.4'}
                        paddingX={'spacing.6'}
                        borderBottomColor={'surface.border.gray.muted'}
                        borderBottomWidth={'thin'}>
                        <Text size="small" weight="semibold" color="surface.text.gray.muted">
                          Benefit values
                        </Text>
                      </Box>
                      <RadioGroup
                        name="options"
                        size="medium"
                        isDisabled={isEditFlow && isNPSComponent}
                        value={`${formData.benefit_amount_settings?.default_option_index}`}
                        onChange={({ value }) =>
                          setBenefitsAmountData({ default_option_index: Number(value) })
                        }>
                        {formData.benefit_amount_settings?.options?.map(
                          (option: number, index: number) => {
                            const isHovered = isHoveredOption === option;
                            return (
                              <MultiBenefitValueRow
                                key={option}
                                isHovered={isHovered}
                                onClick={() =>
                                  setBenefitsAmountData({ default_option_index: index })
                                }>
                                <Box display={'flex'} gap="spacing.3" alignItems={'center'}>
                                  <Radio value={String(index)} />
                                  <Text
                                    size="small"
                                    weight="semibold"
                                    color="surface.text.gray.subtle">
                                    {option}
                                  </Text>
                                  {formData.benefit_amount_settings?.default_option_index ===
                                    index && (
                                    <Box
                                      display={'flex'}
                                      flexDirection={'row'}
                                      gap="spacing.2"
                                      alignItems={'center'}>
                                      <Text
                                        size="xsmall"
                                        weight="semibold"
                                        color="surface.text.gray.muted">
                                        Default amount
                                      </Text>
                                      <Tooltip
                                        content="If an employee is assigned this flexi benefit, default value will automatically be declared unless employee updates the declaration"
                                        zIndex={overlayLayers.nested}
                                        placement="top">
                                        <TooltipInteractiveWrapper
                                          display={'flex'}
                                          alignItems={'center'}>
                                          <InfoIcon
                                            size="xsmall"
                                            color="surface.icon.gray.subtle"
                                          />
                                        </TooltipInteractiveWrapper>
                                      </Tooltip>
                                    </Box>
                                  )}
                                </Box>
                                <Button
                                  size="xsmall"
                                  variant="secondary"
                                  icon={TrashIcon}
                                  color={isHovered ? 'negative' : 'white'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setBenefitsAmountData({
                                      default_option_index:
                                        formData.benefit_amount_settings?.default_option_index ===
                                        index
                                          ? 0
                                          : formData.benefit_amount_settings?.default_option_index,
                                      options: formData.benefit_amount_settings?.options?.filter(
                                        (o: number) => o !== option,
                                      ),
                                    });
                                  }}
                                  onPointerEnter={() => {
                                    setIsHoveredOption(option);
                                  }}
                                  onMouseLeave={() => {
                                    setIsHoveredOption(-1);
                                  }}
                                  accessibilityLabel="delete"
                                />
                              </MultiBenefitValueRow>
                            );
                          },
                        )}
                      </RadioGroup>
                    </Box>
                  ) : null}
                  {isMultiValuesSelected && (
                    <Box marginTop="spacing.5">
                      <Text size="small" color="surface.text.gray.muted">
                        ✅ Employee will be able to select any of the above values for this
                        component: {formatOptions(formData?.benefit_amount_settings?.options || [])}
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
